/**
 * GameMatchupHeader Component
 * Shows game matchup with team info, status, and quarter-by-quarter scores
 */

import type { TrackedParlayLeg } from '@hooks/useTrackedParlays';
import { formatGameClock, formatGameDate } from '@parlay-picker/shared/formatting';
import type { LiveGameScores, QuarterScore } from '@parlay-picker/shared/hooks';
import { parseTeams } from '@parlay-picker/shared/teamParsing';
import { getTeamInfo } from '@parlay-picker/shared/teams';
import React from 'react';

// =============================================================================
// Types
// =============================================================================

interface GameMatchupHeaderProps {
  leg: TrackedParlayLeg;
  quarterScores: QuarterScore[] | null;
  gameScores: LiveGameScores | null;
  isLive: boolean;
  hasLiveData: boolean;
  gameDate?: string | null;
  onPress?: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function GameMatchupHeader({
  leg,
  quarterScores,
  gameScores,
  isLive,
  hasLiveData,
  gameDate,
  onPress,
}: GameMatchupHeaderProps): React.ReactElement {
  // Use authoritative team data from SSE when available (parseTeams can be wrong for player props
  // where leg.team is the player's team, not necessarily the away team)
  const leagueKey = leg.leagueKey;
  const teams = parseTeams(leg);
  const awayTeamInfo = gameScores?.awayTeam
    ? getTeamInfo(gameScores.awayTeam, leagueKey)
    : teams
      ? getTeamInfo(teams[0], leagueKey)
      : null;
  const homeTeamInfo = gameScores?.homeTeam
    ? getTeamInfo(gameScores.homeTeam, leagueKey)
    : teams
      ? getTeamInfo(teams[1], leagueKey)
      : null;

  const statusText = formatGameClock(gameScores);
  const isFinal = !isLive && gameScores?.gameStatus === 'final';
  const gameDateText = formatGameDate(null, gameDate ?? null);

  // Build quarter columns (Q1-Q4 + OT if needed)
  const quarterColumns = React.useMemo(() => {
    const cols = ['Q1', 'Q2', 'Q3', 'Q4'];
    if (quarterScores && quarterScores.length > 4) {
      for (let i = 4; i < quarterScores.length; i++) {
        cols.push(`OT${i - 3}`);
      }
    }
    return cols;
  }, [quarterScores]);

  if (!teams || !awayTeamInfo || !homeTeamInfo) {
    // Fallback for unknown game - still show date if available
    return (
      <div className="bg-theme-surface/50 rounded-lg p-3 border border-theme-border/50">
        {gameDateText && (
          <div className="text-xs text-theme-text-tertiary text-center mb-2">{gameDateText}</div>
        )}
        <div className="text-sm text-theme-text-secondary">Game info unavailable</div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg p-3 border ${
        isLive
          ? 'bg-theme-surface/50 border-theme-primary/30 rebrand-live-game'
          : 'bg-theme-surface/50 border-theme-border/50'
      } ${onPress ? 'cursor-pointer hover:bg-theme-surface-hover/50 transition-colors' : ''}`}
      onClick={onPress}
      role={onPress ? 'button' : undefined}
      tabIndex={onPress ? 0 : undefined}
      onKeyDown={
        onPress
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onPress();
            }
          : undefined
      }
    >
      {/* Team matchup header */}
      <div className="flex items-center justify-between mb-3">
        {/* Away team */}
        <div className="flex items-center gap-2">
          {awayTeamInfo.logo ? (
            <img
              src={awayTeamInfo.logo}
              alt={awayTeamInfo.abbr}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center text-xs font-bold text-theme-text-secondary">
              {awayTeamInfo.abbr}
            </div>
          )}
          <div>
            <div className="font-medium text-sm">{awayTeamInfo.abbr}</div>
            <div className="text-xs text-theme-text-tertiary">
              {awayTeamInfo.name.split(' ').pop()}
            </div>
          </div>
        </div>

        {/* Center: Game date + status/scores */}
        <div className="text-center flex flex-col items-center">
          {/* Game date */}
          {gameDateText && !hasLiveData && (
            <div className="text-xs text-theme-text-tertiary mb-1">{gameDateText}</div>
          )}
          {(statusText || isFinal) && (
            <div className="flex items-center gap-2">
              {statusText && (
                <span className="text-xs text-theme-text-secondary">{statusText}</span>
              )}
              {isLive && (
                <span className="text-[10px] px-1.5 py-0.5 rounded text-white font-bold bg-theme-danger rebrand-live-badge">
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse" />
                  LIVE
                </span>
              )}
              {isFinal && (
                <span className="text-[10px] bg-theme-surface-hover px-1.5 py-0.5 rounded text-white font-medium">
                  FINAL
                </span>
              )}
            </div>
          )}
          <div className="text-lg font-bold mt-1">
            {hasLiveData && gameScores ? (
              <>
                <span className="text-white">{gameScores.awayScore}</span>
                <span className="text-theme-text-tertiary mx-2">-</span>
                <span className="text-white">{gameScores.homeScore}</span>
              </>
            ) : (
              <span className="text-theme-text-tertiary">vs</span>
            )}
          </div>
        </div>

        {/* Home team */}
        <div className="flex items-center gap-2 flex-row-reverse">
          {homeTeamInfo.logo ? (
            <img
              src={homeTeamInfo.logo}
              alt={homeTeamInfo.abbr}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center text-xs font-bold text-theme-text-secondary">
              {homeTeamInfo.abbr}
            </div>
          )}
          <div className="text-right">
            <div className="font-medium text-sm">{homeTeamInfo.abbr}</div>
            <div className="text-xs text-theme-text-tertiary">
              {homeTeamInfo.name.split(' ').pop()}
            </div>
          </div>
        </div>
      </div>

      {/* Quarter-by-quarter score table */}
      {quarterScores && quarterScores.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border/50 overflow-x-auto">
          <table className="w-full text-xs min-w-70">
            <thead>
              <tr className="text-theme-text-tertiary">
                <th className="text-left font-normal w-12"></th>
                {quarterColumns.map((col) => (
                  <th key={col} className="text-center font-normal px-2 py-1">
                    {col}
                  </th>
                ))}
                <th className="text-center font-medium px-2 py-1 text-theme-text-secondary">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Away team row */}
              <tr>
                <td className="text-left font-medium text-theme-text-secondary">
                  {awayTeamInfo.abbr}
                </td>
                {quarterColumns.map((_, idx) => {
                  const qScore = quarterScores[idx];
                  return (
                    <td key={idx} className="text-center px-2 py-1 text-theme-text-secondary">
                      {qScore ? qScore.awayScore : '--'}
                    </td>
                  );
                })}
                <td className="text-center font-bold text-white px-2 py-1">
                  {gameScores?.awayScore ?? '--'}
                </td>
              </tr>
              {/* Home team row */}
              <tr>
                <td className="text-left font-medium text-theme-text-secondary">
                  {homeTeamInfo.abbr}
                </td>
                {quarterColumns.map((_, idx) => {
                  const qScore = quarterScores[idx];
                  return (
                    <td key={idx} className="text-center px-2 py-1 text-theme-text-secondary">
                      {qScore ? qScore.homeScore : '--'}
                    </td>
                  );
                })}
                <td className="text-center font-bold text-white px-2 py-1">
                  {gameScores?.homeScore ?? '--'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
